"use client";

import { ArrowRight, FileText, Play } from "lucide-react";
import type { PaginatedActivityGridItem } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { resolveImageValue } from "../../utils/imageOptions";

/**
 * Document-style card — used by the generic paginated index pages (communiqués,
 * publications, …) AND by the home "latest videos" strip. Rendered inside a
 * responsive grid. Each card is a clean, equal-height card: a small type/meta
 * line, the title as a prominent brand-blue link, an optional two-line
 * description and a "Consulter"/"Regarder" affordance pinned to the bottom.
 *
 * Optional thumbnail (e.g. video miniatures on /actualites/videos and the home
 * video strip). Document lists without an image field (communiqués,
 * publications) fall through to the imageless card — the image strip is only
 * rendered when a real key is resolved, so nothing changes for them. When a
 * thumbnail IS present a centered play badge reads the tile as a video and the
 * bottom affordance switches from "Consulter" to "Regarder".
 *
 * On the site palette, with the white-card / slate-200 border / soft blue
 * shadow convention shared with `InformationHub` and `Gallery`.
 */
export function DocumentCard({ item }: { item: PaginatedActivityGridItem }) {
  const locale = useLocale();
  const href = resolveLinkHref(item.href);
  const meta = (item.meta ?? "").toString().trim();
  const description = (item.description ?? "").toString().trim();
  const { src: imageSrc } = resolveImageValue(item.imageSrc);
  const hasImage = Boolean(imageSrc);

  const inner = (
    <article className="apa-card flex h-full flex-col overflow-hidden transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[var(--shadow-e2)] group-hover:[outline-color:rgba(0,184,98,.4)]">
      {hasImage ? (
        <div className="relative aspect-video w-full overflow-hidden bg-slate-100">
          <RuntimeImage
            src={imageSrc as string}
            alt={item.imageAlt || item.title || ""}
            className="absolute inset-0 h-full w-full object-cover transition duration-200 group-hover:scale-[1.02]"
          />
          <span
            className="absolute inset-0 grid place-items-center"
            aria-hidden="true"
          >
            <span className="grid h-12 w-12 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm transition group-hover:bg-[#00B862]">
              <Play className="h-5 w-5 translate-x-[1px] fill-current" />
            </span>
          </span>
        </div>
      ) : null}
      <div className={`flex flex-1 flex-col p-4 sm:p-5 ${hasImage ? "pt-4 sm:pt-4" : ""}`}>
      <div className="flex items-center gap-2 text-[12px] xl:text-[13px] font-medium uppercase tracking-wide text-[#6B7280]">
        <span
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-[var(--color-forest-100)] text-[var(--color-forest-600)]"
          aria-hidden="true"
        >
          <FileText className="h-4 w-4" />
        </span>
        {meta ? <span className="truncate">{meta}</span> : null}
      </div>
      <h3 className="mt-3 text-[16px] xl:text-[18px] font-semibold leading-snug text-[#00B862] group-hover:underline">
        {item.title}
      </h3>
      {description ? (
        <p className="mt-2 line-clamp-2 text-[14px] xl:text-[15px] leading-snug text-[#121212]">
          {description}
        </p>
      ) : null}
      <span className="mt-auto inline-flex items-center gap-1.5 self-start pt-4 text-[13px] xl:text-[14px] font-semibold text-[#00B862]">
        {hasImage ? t(locale, "watch") : t(locale, "view")}
        <ArrowRight className="h-4 w-4 text-[#FFC105] transition group-hover:translate-x-0.5" />
      </span>
      </div>
    </article>
  );

  return href ? (
    <RuntimeLink href={href} className="group block h-full">
      {inner}
    </RuntimeLink>
  ) : (
    <div className="group h-full">{inner}</div>
  );
}
